import { ReactNode, useCallback, useEffect, useRef, useState } from 'react'

import classNames from 'classnames'

import { Icon } from '@/Atomic/Atoms/Icon'

import './NavTabs.scss'

export interface NavTabItem {
  id: string
  title: string
  icon?: string
  count?: number
  content?: ReactNode
  disabled?: boolean
}

interface NavTabsProps {
  tabs: NavTabItem[]
  className?: string
  variant?: 'default' | 'large'
  activeTab?: string
  onTabChange?: (id: string) => void
  renderPanels?: boolean
}

const SCROLL_AMOUNT = 200

export const NavTabs: React.FC<NavTabsProps> = ({
  tabs,
  className,
  variant = 'default',
  renderPanels = true,
  activeTab: controlledActiveTab,
  onTabChange,
}) => {
  const firstEnabledTab = tabs.find((tab) => !tab.disabled)?.id
  const [internalActiveTab, setInternalActiveTab] = useState(firstEnabledTab)
  const isControlled = controlledActiveTab !== undefined
  const hasInternalActiveTab = tabs.some(
    (tab) => tab.id === internalActiveTab && !tab.disabled,
  )
  const activeTab = isControlled
    ? controlledActiveTab
    : hasInternalActiveTab
      ? internalActiveTab
      : firstEnabledTab

  const handleTabChange = (id: string) => {
    if (!isControlled) setInternalActiveTab(id)
    onTabChange?.(id)
  }
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)
  const listRef = useRef<HTMLUListElement>(null)

  const updateArrows = useCallback(() => {
    const el = listRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 0)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1)
  }, [])

  useEffect(() => {
    const el = listRef.current
    if (!el) return
    updateArrows()
    el.addEventListener('scroll', updateArrows, { passive: true })
    const observer = new ResizeObserver(updateArrows)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', updateArrows)
      observer.disconnect()
    }
  }, [updateArrows])

  const scrollLeft = () => {
    listRef.current?.scrollBy({ left: -SCROLL_AMOUNT, behavior: 'smooth' })
  }

  const scrollRight = () => {
    listRef.current?.scrollBy({ left: SCROLL_AMOUNT, behavior: 'smooth' })
  }

  return (
    <>
      <div
        className={classNames(
          'nav-tabs-wrapper',
          `nav-tabs-wrapper--${variant}`,
        )}
      >
        {canScrollLeft && (
          <button
            className="nav-tabs-scroll-arrow nav-tabs-scroll-arrow--left"
            onClick={scrollLeft}
            aria-label="Scroll tabs left"
            tabIndex={-1}
          >
            <Icon icon="chevron-left" color="default" />
          </button>
        )}

        <ul ref={listRef} className={classNames('nav-tabs-list', className)}>
          {tabs.map((tab) => (
            <li key={tab.id}>
              <button
                type="button"
                className={classNames('nav-tabs-button', {
                  'is-active': activeTab === tab.id,
                  'is-disabled': tab.disabled,
                })}
                onClick={() => !tab.disabled && handleTabChange(tab.id)}
                disabled={tab.disabled}
                data-testid={`tab-${tab.id}`}
              >
                {tab.icon && (
                  <Icon
                    icon={tab.icon}
                    size="sm"
                    className="nav-tabs-button-icon"
                  />
                )}
                <span className="nav-tabs-button-title">{tab.title}</span>
                {tab.count !== undefined && (
                  <span className="nav-tabs-button-count">({tab.count})</span>
                )}
              </button>
            </li>
          ))}
        </ul>

        {canScrollRight && (
          <button
            className="nav-tabs-scroll-arrow nav-tabs-scroll-arrow--right"
            onClick={scrollRight}
            aria-label="Scroll tabs right"
            tabIndex={-1}
          >
            <Icon icon="chevron-right" color="default" />
          </button>
        )}
      </div>

      {renderPanels && (
        <div className="nav-tabs-panel-group">
          {tabs.map((tab) => (
            <div
              key={tab.id}
              className={classNames('nav-tabs-panel', {
                'is-visible': activeTab === tab.id,
              })}
            >
              {activeTab === tab.id && tab.content}
            </div>
          ))}
        </div>
      )}
    </>
  )
}
